import { Button } from '@/components/ui'
import { SEO } from '@/components/seo'

const certificationBadges = [
  {
    label: 'EU Data Centres',
    subtext: 'Your data stays in the EU',
    icon: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  },
  {
    label: 'Enterprise-Grade Encryption',
    subtext: 'AES-256 encryption',
    icon: 'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z',
  },
  {
    label: 'GDPR Compliant',
    subtext: 'GDPR standards followed & ICO registered',
    icon: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z',
  },
  {
    label: 'High Availability',
    subtext: 'Continuous availability architecture',
    icon: 'M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM17 13l-5 5-5-5h3V9h4v4h3z',
  },
  {
    label: '99.9% Uptime',
    subtext: 'Enterprise SLA',
    icon: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
  },
  {
    label: 'FCA Ready',
    subtext: 'Built for regulated firms',
    icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
  },
]

const securityChecklist = [
  {
    question: 'Is my data stored in the EU?',
    answer: 'Yes',
    detail: 'EU-based data centres only',
  },
  {
    question: 'Is data encrypted?',
    answer: 'Yes',
    detail: 'AES-256 at rest, TLS 1.3 in transit',
  },
  {
    question: 'Can I control who accesses what?',
    answer: 'Yes',
    detail: 'Role-based access controls',
  },
  {
    question: 'Is multi-factor authentication available?',
    answer: 'Yes',
    detail: 'TOTP authenticator for all users',
  },
  {
    question: 'Is there high availability?',
    answer: 'Yes',
    detail: 'Continuous availability architecture',
  },
  {
    question: 'Is there an uptime guarantee?',
    answer: 'Yes',
    detail: '99.9% uptime SLA',
  },
  {
    question: 'Are you GDPR compliant?',
    answer: 'Yes',
    detail: 'GDPR standards followed & ICO registered',
  },
  {
    question: 'Can I export my data?',
    answer: 'Yes',
    detail: 'Full data portability on request',
  },
  {
    question: 'Do you have a security policy?',
    answer: 'Yes',
    detail: 'Available on request',
  },
  {
    question: 'Is there support for security issues?',
    answer: 'Yes',
    detail: 'Priority security support',
  },
]

const infoCards = [
  {
    title: 'Your Client Data',
    points: [
      'Only accessible by authorised users in your firm',
      'Encrypted to enterprise-grade standards',
      'Never sold or shared with third parties',
      'Continuous availability architecture',
    ],
  },
  {
    title: 'Your Compliance',
    points: [
      'Supports your FCA compliance obligations',
      'Full audit trail of all user actions',
      'Data Processing Agreement available',
      'Ready for your compliance review',
    ],
  },
  {
    title: 'Your Peace of Mind',
    points: [
      'Regular third-party security testing',
      'Dedicated security team',
      'Documented incident response plan',
      'Transparent communication',
    ],
  },
]

export default function SecurityPage() {
  return (
    <>
      <SEO
        title="Security Overview | WealthSpace"
        description="Learn how WealthSpace protects your client data with enterprise-grade encryption, EU data centres, and GDPR compliance."
        keywords="financial adviser data security, IFA data protection, EU data residency, GDPR compliant software, secure AI platform, FCA ready"
        url="/security"
      />

      {/* Hero Section */}
      <section className="py-[80px] px-6 max-w-[1200px] mx-auto text-center">
        <h1 className="font-serif text-[clamp(36px,5vw,52px)] font-normal leading-[1.2] tracking-[-0.02em] text-brand-light mb-4">
          Your Data, <span className="text-accent-teal italic">Protected</span>
        </h1>
        <p className="text-xl text-accent-teal font-medium mb-8">
          Security & Data Protection at WealthSpace
        </p>
      </section>

      {/* Trust Box */}
      <section className="px-6 pb-12">
        <div className="max-w-[900px] mx-auto">
          <div
            className="rounded-2xl p-8 text-center text-white"
            style={{
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
            }}
          >
            <p className="text-[17px] leading-[1.7]">
              WealthSpace takes the security of your client data as seriously as you do. We've built
              our platform with UK financial services in mind, meeting the standards expected by the
              FCA and your compliance team.
            </p>
          </div>
        </div>
      </section>

      {/* Certification Badges Grid */}
      <section className="py-12 px-6 max-w-[1000px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {certificationBadges.map((badge, index) => (
            <div
              key={index}
              className="bg-[#f0fdfa] border-2 border-accent-teal rounded-xl p-5 text-center"
            >
              <div className="w-11 h-11 bg-accent-teal rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 fill-white" viewBox="0 0 24 24">
                  <path d={badge.icon} />
                </svg>
              </div>
              <span className="text-[13px] font-semibold text-[#1a1a2e] block leading-tight mb-1">
                {badge.label}
              </span>
              <span className="text-[11px] text-[#64748b]">{badge.subtext}</span>
            </div>
          ))}
        </div>
      </section>

      {/* AI That Respects Your Data */}
      <section className="py-12 px-6">
        <div className="max-w-[900px] mx-auto">
          <div
            className="rounded-2xl p-6 flex items-start gap-5"
            style={{
              background: 'linear-gradient(145deg, #1a1a2e 0%, #2d2d4a 100%)',
            }}
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, #14b8a6, #8b5cf6)',
              }}
            >
              <svg className="w-7 h-7 fill-white" viewBox="0 0 24 24">
                <path d="M21 10.12h-6.78l2.74-2.82c-2.73-2.7-7.15-2.8-9.88-.1-2.73 2.71-2.73 7.08 0 9.79s7.15 2.71 9.88 0C18.32 15.65 19 14.08 19 12.1h2c0 1.98-.88 4.55-2.64 6.29-3.51 3.48-9.21 3.48-12.72 0-3.5-3.47-3.53-9.11-.02-12.58s9.14-3.47 12.65 0L21 3v7.12zM12.5 8v4.25l3.5 2.08-.72 1.21L11 13V8h1.5z" />
              </svg>
            </div>
            <div>
              <h3 className="font-serif text-lg text-accent-teal mb-2">AI That Respects Your Data</h3>
              <p className="text-[15px] leading-[1.6] text-[#e8e6e1]">
                Our AI features are powered by{' '}
                <strong className="text-accent-teal">enterprise-grade cloud architecture</strong> that
                keeps your data secure. Your client information{' '}
                <strong className="text-accent-teal">never leaves our secure environment</strong> and is{' '}
                <strong className="text-accent-teal">never used to train AI models</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Security Checklist */}
      <section className="py-16 px-6 max-w-[900px] mx-auto">
        <h2 className="font-serif text-base font-semibold text-accent-teal uppercase tracking-[1.5px] mb-2 pb-4 border-b-2 border-accent-teal">
          Security Checklist
        </h2>
        <p className="text-[#64748b] text-sm mb-8">
          The questions your compliance team will ask — answered.
        </p>

        <div className="space-y-0">
          {securityChecklist.map((item, index) => (
            <div
              key={index}
              className="flex items-center py-4 border-b border-[#e2e8f0] last:border-b-0"
            >
              <span className="w-7 h-7 bg-[#10b981] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              </span>
              <span className="flex-1 text-[15px] text-[#1a1a2e]">{item.question}</span>
              <span className="text-right min-w-[100px]">
                <span className="text-[15px] text-accent-teal font-semibold block">
                  {item.answer}
                </span>
                <span className="text-[12px] text-[#64748b] block mt-0.5">{item.detail}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* What This Means For You */}
      <section className="py-16 px-6 max-w-[1000px] mx-auto">
        <h2 className="font-serif text-base font-semibold text-accent-teal uppercase tracking-[1.5px] mb-8 pb-4 border-b-2 border-accent-teal">
          What This Means For You
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {infoCards.map((card, index) => (
            <div
              key={index}
              className="bg-[#f8fafc] border border-[#e2e8f0] border-l-4 border-l-accent-teal rounded-lg p-5"
            >
              <h4 className="text-[15px] font-semibold text-accent-teal mb-4">{card.title}</h4>
              <ul className="space-y-2">
                {card.points.map((point, pointIndex) => (
                  <li
                    key={pointIndex}
                    className="text-[13px] text-[#475569] pl-5 relative leading-[1.4]"
                  >
                    <span className="absolute left-0 text-[#10b981] font-semibold">&#10003;</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Enterprise Security Pack Note */}
        <div className="bg-[#fef3c7] border-l-4 border-l-[#f59e0b] rounded-r-lg p-4 mb-8">
          <p className="text-[14px] text-[#92400e] leading-[1.5]">
            <strong className="text-[#78350f]">Need more technical detail?</strong> We have a
            comprehensive Enterprise Security Pack for IT teams and larger organisations completing
            formal vendor security assessments. Contact us to request a copy.
          </p>
        </div>
      </section>

      {/* CTA Box */}
      <section className="py-16 px-6 max-w-[900px] mx-auto">
        <div className="bg-[#1a1a2e] rounded-2xl p-8 text-center">
          <h3 className="font-serif text-xl text-accent-teal mb-3">Questions? We're Here to Help</h3>
          <p className="text-[14px] text-[#94a3b8] mb-2">
            For security questions or to request our Data Processing Agreement:
          </p>
          <p className="mb-2">
            <a
              href="mailto:support@wealthspace.ai"
              className="text-accent-teal font-semibold text-base hover:underline"
            >
              support@wealthspace.ai
            </a>
          </p>
          <p className="text-[12px] text-[#94a3b8] mt-4">
            Phone:{' '}
            <a
              href="tel:01225541226"
              className="text-accent-teal font-semibold hover:underline"
            >
              01225 541226
            </a>
          </p>
        </div>
      </section>

      {/* Alternative Get in Touch CTA */}
      <section className="py-16 px-6 max-w-[900px] mx-auto text-center">
        <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.08] rounded-3xl py-14 px-10">
          <h2 className="font-serif text-[36px] font-normal text-brand-light tracking-[-0.02em] mb-4">
            Want to discuss <span className="text-accent-teal italic">security</span>?
          </h2>
          <p className="text-lg text-brand-muted leading-[1.7] max-w-[500px] mx-auto mb-8">
            Our team is happy to discuss our security practices and answer any questions you have.
          </p>
          <Button href="/contact" size="lg">
            Get in touch
            <svg
              className="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Button>
        </div>
      </section>
    </>
  )
}
